/**
 * 版本检查和缓存清理工具
 * 支持通过JS文件名Hash检测更新（无需手动维护版本号）
 */

/**
 * 提取 HTML 中所有 JS 文件的 hash
 * @param {string} html - HTML 内容
 * @returns {string} - 排序后的 hash 字符串（逗号分隔）
 */
function extractJsHashes(html) {
  const scriptRegex = /<script[^>]+src=["']([^"']+)["']/g
  const hashes = []
  let match
  
  while ((match = scriptRegex.exec(html)) !== null) {
    const src = match[1]
    // 提取 hash，如 app.abc123.js -> abc123
    // 支持多种格式：app.abc123.js, chunk-vendors.def456.js 等
    const hashMatch = src.match(/\.([a-f0-9]{8,})\.js/)
    if (hashMatch) {
      hashes.push(hashMatch[1])
    }
  }
  
  // 排序后拼接，确保顺序一致
  return hashes.sort().join(',')
}

/**
 * 清除所有缓存（包括移动端）
 */
function clearAllCaches() {
  // 清除 Cache API 缓存
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name)
      })
    })
  }
  
  // 清除 Service Worker（如果存在）
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        registration.unregister()
      })
    })
  }
}

/**
 * 清除 localStorage 和 sessionStorage（保留重要数据）
 * @returns {Object} - 保存的重要数据
 */
function clearStorageWithPreserve() {
  // 保存重要数据
  const token = localStorage.getItem('token')
  const user = localStorage.getItem('user')
  const savedCredentials = localStorage.getItem('savedCredentials') // 记住密码功能
  
  // 清除所有存储
  localStorage.clear()
  sessionStorage.clear()
  
  // 恢复重要数据
  if (token) localStorage.setItem('token', token)
  if (user) localStorage.setItem('user', user)
  if (savedCredentials) localStorage.setItem('savedCredentials', savedCredentials)
  
  return { token, user, savedCredentials }
}

/**
 * 强制刷新页面
 */
function forceReload() {
  // 使用 reload(true) 强制从服务器重新加载
  if (window.location.reload) {
    window.location.reload(true)
  } else {
    // 降级方案：直接跳转（添加时间戳确保获取最新版本）
    const timestamp = new Date().getTime()
    window.location.href = window.location.href.split('?')[0] + '?v=' + timestamp
  }
}

/**
 * 检查是否有更新（通过JS文件名Hash）
 * @returns {Promise<boolean>} - 是否有更新
 */
async function checkForUpdatesByHash() {
  const storedHashes = localStorage.getItem('app_js_hashes')
  
  try {
    // 添加时间戳确保不缓存
    const response = await fetch(window.location.origin + '/index.html?v=' + new Date().getTime(), {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    })
    
    if (!response.ok) {
      console.debug('获取 index.html 失败:', response.status)
      return false
    }
    
    const html = await response.text()
    const currentHashes = extractJsHashes(html)
    
    // 如果没有存储的 hash，说明是首次访问，保存当前 hash
    if (!storedHashes) {
      localStorage.setItem('app_js_hashes', currentHashes)
      return false
    }
    
    // 对比 hash，如果不同说明有更新
    if (storedHashes !== currentHashes) {
      console.log('检测到新版本（JS文件Hash已变化）')
      return true
    }
    
    return false
  } catch (error) {
    // 静默失败，不影响正常使用
    console.debug('版本检查失败:', error)
    return false
  }
}

/**
 * 处理版本更新（清除缓存并刷新）
 */
function handleVersionUpdate() {
  console.log('检测到新版本，清除缓存并刷新页面...')
  
  // 清除存储（保留重要数据）
  clearStorageWithPreserve()
  
  // 清除所有缓存
  clearAllCaches()
  
  // 强制刷新页面
  forceReload()
}

/**
 * 初始化版本检查（页面加载时调用）
 */
export function initVersionCheck() {
  // 立即检查一次是否有更新
  checkForUpdatesByHash().then(hasUpdate => {
    if (hasUpdate) {
      handleVersionUpdate()
      return false // 阻止继续执行
    }
    
    // 保存当前 hash（如果还没有保存）
    if (!localStorage.getItem('app_js_hashes')) {
      // 从当前页面的 script 标签提取 hash
      const scripts = document.querySelectorAll('script[src]')
      const hashes = []
      scripts.forEach(script => {
        const src = script.getAttribute('src')
        if (src) {
          const hashMatch = src.match(/\.([a-f0-9]{8,})\.js/)
          if (hashMatch) {
            hashes.push(hashMatch[1])
          }
        }
      })
      if (hashes.length > 0) {
        localStorage.setItem('app_js_hashes', hashes.sort().join(','))
      }
    }
    
    // 添加版本信息到页面（用于调试）
    const currentHashes = localStorage.getItem('app_js_hashes')
    if (currentHashes) {
      document.documentElement.setAttribute('data-app-hash', currentHashes.substring(0, 16))
    }
  })
}

/**
 * 定期检查版本更新（适用于移动端）
 * @param {number} interval - 检查间隔（毫秒），默认5分钟
 */
export function startPeriodicVersionCheck(interval = 5 * 60 * 1000) {
  // 每指定时间检查一次是否有新版本
  setInterval(() => {
    checkForUpdatesByHash().then(hasUpdate => {
      if (hasUpdate) {
        handleVersionUpdate()
      }
    })
  }, interval)
  
  // 页面可见性变化时也检查更新（用户切换回页面时）
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => {
      // 当页面从隐藏变为可见时，检查更新
      if (!document.hidden) {
        checkForUpdatesByHash().then(hasUpdate => {
          if (hasUpdate) {
            handleVersionUpdate()
          }
        })
      }
    })
    
    // 页面获得焦点时也检查更新
    window.addEventListener('focus', () => {
      checkForUpdatesByHash().then(hasUpdate => {
        if (hasUpdate) {
          handleVersionUpdate()
        }
      })
    })
  }
}

/**
 * 手动触发版本检查（可用于页面可见时检查）
 */
export async function manualVersionCheck() {
  const hasUpdate = await checkForUpdatesByHash()
  if (hasUpdate) {
    handleVersionUpdate()
    return true
  }
  return false
}

